import { toaster } from "@/components/ui/toaster";
import { EstadosSolicitudEstablecimiento } from "@/constants/enums/status/entity-status-ids";
import { AppRoutes } from "@/constants/routes";
import {
  EstablishmentFieldRequestDetail,
  getEstablishmentFieldRequestDetail,
  patchEstablishmentFieldRequest,
} from "@/services/establishment-field-request.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { Dispatch, SetStateAction } from "react";
import { FieldValues } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";

const ARTICLE: Record<string, string> = { alta: "el", modificación: "la", baja: "la" };
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const article = (op: string) => ARTICLE[op] ?? "la";

interface FetchDetailProps {
  id: string | undefined;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setDetail: Dispatch<SetStateAction<EstablishmentFieldRequestDetail | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchEstablishmentFieldRequestDetailHandler = async ({
  id,
  setLoading,
  setDetail,
  navigate,
  fetchPermissions,
}: FetchDetailProps) => {
  if (!id) return;
  try {
    setLoading(true);
    const response = await getEstablishmentFieldRequestDetail(id);
    setDetail(response);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ESTABLISMENT_FIELD_REQUESTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo cargar el detalle de la solicitud."),
      });
    }
  } finally {
    setLoading(false);
  }
};

interface ConfirmDetailProps {
  id: number;
  setLoading: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  tipoLabel: string;
  operacionLabel: string;
  pendingTipoEstablecimiento?: number | null;
}

export const confirmEstablishmentFieldRequestHandler = async ({
  id,
  setLoading,
  navigate,
  fetchPermissions,
  tipoLabel,
  operacionLabel,
  pendingTipoEstablecimiento,
}: ConfirmDetailProps) => {
  try {
    setLoading(true);
    await patchEstablishmentFieldRequest(id, {
      id,
      idEstado: EstadosSolicitudEstablecimiento.Confirmada,
      ...(pendingTipoEstablecimiento != null && { tipoEstablecimiento: pendingTipoEstablecimiento }),
    });
    toaster.create({
      type: "success",
      title: `${cap(operacionLabel)} de ${tipoLabel} confirmada con éxito.`,
    });
    navigate(AppRoutes.ESTABLISMENT_FIELD_REQUESTS);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          `No se pudo confirmar ${article(operacionLabel)} ${operacionLabel} de ${tipoLabel}.`,
        ),
      });
    }
  } finally {
    setLoading(false);
  }
};

interface RejectDetailProps {
  id: number;
  event: FieldValues | undefined;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  tipoLabel: string;
  operacionLabel: string;
}

export const rejectEstablishmentFieldRequestHandler = async ({
  id,
  event,
  setLoading,
  setOpenModal,
  navigate,
  fetchPermissions,
  tipoLabel,
  operacionLabel,
}: RejectDetailProps) => {
  try {
    setLoading(true);
    await patchEstablishmentFieldRequest(id, {
      id,
      idEstado: EstadosSolicitudEstablecimiento.Rechazada,
      motivoRechazo: event?.request_detail ?? "",
    });
    toaster.create({
      type: "success",
      title: `${cap(operacionLabel)} de ${tipoLabel} rechazada exitosamente.`,
    });
    setOpenModal(false);
    navigate(AppRoutes.ESTABLISMENT_FIELD_REQUESTS);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          `No se ha podido rechazar ${article(operacionLabel)} ${operacionLabel} de ${tipoLabel}.`,
        ),
      });
    }
  } finally {
    setLoading(false);
  }
};
